import { FC, useState } from 'react';
import { NotebookPen } from 'lucide-react';
import {
  useGetUserNotesForObjectiveQuery,
  useGetUserNotesForLessonQuery,
  useGetUserNotesForCourseQuery,
} from '@teachur/http-client';
import { Drawer } from '@new/common/ui/Drawer/Drawer';
import { Button } from '@new/common/ui/Button/Button';
import { NotesList } from '../NotesList/NotesList';
import { AddNoteForm } from '../AddNoteForm/AddNoteForm';

type NotesRel = 'objective' | 'lesson' | 'course';

interface NotesPanelProps {
  relId: number;
  rel: NotesRel;
}

const useNotesQuery = (rel: NotesRel, relId: number) => {
  const objectiveResult = useGetUserNotesForObjectiveQuery(rel === 'objective' ? relId : 0);
  const lessonResult = useGetUserNotesForLessonQuery(rel === 'lesson' ? relId : 0);
  const courseResult = useGetUserNotesForCourseQuery(rel === 'course' ? relId : 0);

  if (rel === 'objective') return objectiveResult;
  if (rel === 'lesson') return lessonResult;
  return courseResult;
};

export const NotesPanel: FC<NotesPanelProps> = ({ relId, rel }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { data: notesData } = useNotesQuery(rel, relId);

  const showEditFrom = rel === 'objective';

  return (
    <>
      <Button
        variant="primary"
        size="icon"
        onClick={() => setIsOpen(true)}
        aria-label="Open notes"
        title="Open notes"
        className="fixed bottom-3 md:top-1/2 right-4 -translate-y-1/2 z-50 rounded-full! shadow-lg w-10 h-10! md:w-12 md:h-12! p-0!"
        leftIcon={<NotebookPen size={20} />}
      />

      <Drawer
        open={isOpen}
        onClose={setIsOpen}
        title="My Notes"
        footer={showEditFrom ? <AddNoteForm objectiveId={relId} /> : undefined}
      >
        <div className="flex flex-col p-4 gap-4">
          {notesData?.name && (
            <p className="[font-family:var(--font-montserrat)] [font-size:var(--fs-l4)] text-(--neutral-black-NB500) m-0">
              {notesData.name}
            </p>
          )}

          <div className="flex-1 overflow-y-auto">
            <NotesList data={notesData} rel={rel} />
          </div>
        </div>
      </Drawer>
    </>
  );
};
